import useSettings from "./useSettings";
import { SettingsHeader, Section, Field, Loading } from "./_shared";
import { Shield, ExternalLink } from "lucide-react";

/**
 * Cloudflare Turnstile settings.
 * - Site key is public (baked into the widget on load).
 * - Secret key is stored in the DB (redacted in /api/settings) and used by
 *   the backend to verify tokens with Cloudflare's Siteverify API.
 * - Leave both empty to disable Turnstile — forms keep working normally.
 */
export default function TurnstileSettingsAdmin() {
  const { settings, set, save, saving, saved } = useSettings();
  if (!settings) return <Loading />;

  const configured = !!(settings.turnstile_site_key || "").trim();

  return (
    <div data-testid="cms-settings-turnstile">
      <SettingsHeader
        title="Cloudflare Turnstile"
        description="Protección anti-bots (CAPTCHA alternativo) para el login del CMS y el formulario de contacto. La newsletter usa doble opt-in y no lo necesita."
        onSave={save}
        saving={saving}
        saved={saved}
      />

      <div className={`p-4 mb-6 border flex items-start gap-3 text-sm ${configured ? "bg-green-50 border-green-200 text-green-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
        <Shield className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">{configured ? "Turnstile activo" : "Turnstile no configurado"}</p>
          <p className="text-xs mt-1">
            {configured
              ? "Los 2 formularios (login del CMS y contacto) validan cada envío contra Cloudflare."
              : "Deje los campos vacíos y los formularios seguirán funcionando sin CAPTCHA. Rellene para activar la protección."}
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <Section title="Claves del widget">
          <div className="space-y-4">
            <Field
              label="Site Key (pública) *"
              value={settings.turnstile_site_key}
              onChange={(v) => set("turnstile_site_key", v)}
            />
            <Field
              label="Secret Key (privada) *"
              value={settings.turnstile_secret_key}
              onChange={(v) => set("turnstile_secret_key", v)}
              type="password"
            />
            <p className="text-xs text-gray-500">
              Cree/rote sus claves en el <a
                href="https://dash.cloudflare.com/?to=/:account/turnstile"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C8102E] hover:underline inline-flex items-center gap-1"
              >
                Cloudflare Dashboard <ExternalLink className="w-3 h-3" />
              </a>. Al guardar, la Secret Key ya <b>no se muestra en el sitio público</b> — solo se ve aquí.
            </p>
          </div>
        </Section>

        <Section title="Formularios protegidos">
          <ul className="text-sm text-gray-700 space-y-2">
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-[#C8102E] rounded-full"></span> Login del CMS (<code>/admin</code>)</li>
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-[#C8102E] rounded-full"></span> Formulario de contacto (<code>/contacto</code>)</li>
          </ul>
          <p className="text-xs text-gray-500 mt-3">
            La suscripción a la newsletter se protege mediante <b>doble opt-in</b> (Acumbamail),
            que ya bloquea el abuso automatizado — no requiere CAPTCHA.
          </p>
        </Section>

        <Section title="¿Cómo funciona la degradación?">
          <p className="text-sm text-gray-600">
            Turnstile se activa solo si <b>ambas claves</b> están presentes. Si borra cualquiera de las dos, el
            widget desaparece del sitio y los formularios envían sin verificación. Útil para pruebas o
            entornos internos.
          </p>
        </Section>
      </div>
    </div>
  );
}
